import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from "next";
import parlors from "@/constants/parlors_detail.json";

import numberToLocalString from "@/utils/numberToLocalString";
import NotFoundPage from "pages/404";
import RichSearch from "@/components/common/RichSearch";
import { FUNERAL_SIGUNGU } from "goi_common";
import { getThreeClosestParlorFrom } from "@/containers/Parlor/ParlorDetailContainer/utils/getThreeClosestParlorFrom";
import dayjs from "dayjs";
import { Box } from "@chakra-ui/react";
import ParlorRequestContainer from "@/containers/Parlor/ParlorRequestContainer";
import { richSearchData } from "@/constants/richSearch";
import { BinsoObject, ParlorDetailProps } from "@/types/parlor";

import { skipDevBuildInGetStaticPaths } from "@/utils/skipDevBuildInGetStaticPaths";

export const getStaticPaths: GetStaticPaths = async () => {
  const skipBuildResult = skipDevBuildInGetStaticPaths() as GetStaticPathsResult;
  if (skipBuildResult) {
    return skipBuildResult;
  }

  const paths = parlors.map((parlor) => ({
    params: {
      id: String(parlor.id),
    },
  }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const id = params?.id;
  let parlor;
  let district;
  let sigungu;
  let name = "";
  let description = "";
  let closestParlorsByService = {};

  if (id) {
    parlor = parlors.find((parlor) => parlor.id === Number(id));
    if (parlor) {
      name = parlor.companyname;
      sigungu = FUNERAL_SIGUNGU.get(Number(parlor?.sido))?.get(Number(parlor?.sigungu));
      district = parlor.address.slice(0, 2);
      if (parlor.address.split(" ")[0].length == 4)
        district = parlor.address[0] + parlor.address[2];

      let binsoString = "";
      parlor.binsos.forEach((binso: BinsoObject) => {
        if (binso.pyeong) {
          binsoString += `${binso.pyeong.toFixed()}평형 ${numberToLocalString(binso.charge)}원, `;
        }
      });

      description =
        `${name}(${sigungu})` +
        " 장례, " +
        `${name}(${sigungu})` +
        " 장례식 비용, " +
        `${name}(${sigungu})` +
        " 3일장 장례 비용, " +
        `${name}(${sigungu})` +
        " 평균 장례 비용, " +
        `${name}(${sigungu})` +
        " 빈소 유형\n" +
        binsoString.slice(0, binsoString.length - 2) +
        "\n" +
        `${name}(${sigungu})` +
        "예상 장례 비용: " +
        parlor.expected_charge_sum["무빈소"].toLocaleString() +
        "원 ~ " +
        parlor.expected_charge_sum["일반장"].toLocaleString() +
        "원 ";

      closestParlorsByService = {
        무빈소: getThreeClosestParlorFrom(parlor as any, "무빈소"),
        가족장: getThreeClosestParlorFrom(parlor as any, "가족장"),
        일반장: getThreeClosestParlorFrom(parlor as any, "일반장"),
      };
    }
  }

  return {
    props: {
      defaultParlor: parlor,
      pageDescription: description,
      pageKeywords: `${name}(${sigungu}), ${name}(${sigungu}) 장례 비용, ${district} 장례 견적, ${name} 평균 장례 비용`,
      pageOgTitle: `${name}(${sigungu}) 장례식 비용 - [장례의 모든 것, 고이장례연구소]`,
      pageOgDescription: description,
      pageOgImage: parlor?.images[0]?.url || `${process.env.NEXT_PUBLIC_CDN_URL}/ogimage.png`,
      hasError: !parlor,
      closestParlorsByService,
    },
  };
};

function ParlorRequest({
  defaultParlor,

  hasError,
}: {
  defaultParlor: ParlorDetailProps;

  hasError: boolean;
}) {
  const { howtoRichSearch, carouselRichSearch, reviewResult76 } = richSearchData.parlor;
  const { faqList, reviewResult } = richSearchData.common;
  if (hasError) return <NotFoundPage />;
  const today = dayjs().format("YYYY-MM-DD");
  return (
    <>
      <Box position="absolute" marginTop="-90px">
        <time dateTime={today} className="white se_publishDate pcol2 date">
          {today}
        </time>
      </Box>

      <RichSearch key="faqs" data={faqList} />
      <RichSearch key="howto" data={howtoRichSearch} />
      <RichSearch key="carousel" data={carouselRichSearch} />
      <RichSearch
        key="review"
        data={String(defaultParlor.id) === "76" ? reviewResult76 : reviewResult}
      />
      <ParlorRequestContainer parlor={defaultParlor} />
    </>
  );
}

export default ParlorRequest;
